import React, { useState, useMemo } from 'react';
import {
  Table,
  Tag,
  Select,
  Input,
  Button,
  Space,
  Empty,
  Typography,
  Tooltip,
  message,
  theme,
} from 'antd';
import {
  CheckOutlined,
  FilePdfOutlined,
  ReloadOutlined,
  SearchOutlined,
  WarningOutlined,
  ThunderboltFilled,
  FileAddOutlined,
} from '@ant-design/icons';
import axios from 'axios';
import { API_PATH } from 'config';
import { getAccessTokenApi } from 'api/auth';
import HallazgosCerrarModal from './HallazgosCerrarModal';
import HallazgosSeveridadModal from './HallazgosSeveridadModal';
import RankingMensualModal from './RankingMensualModal';
import HallazgoTracker from './HallazgoTracker';
import GrupoRow from './GrupoRow';
import FranjaPatrones from './FranjaPatrones';
import PresupuestoFromHallazgosDrawer from './PresupuestoFromHallazgosDrawer';
import { useTrazabilidadDrawer } from 'providers/Trazabilidad/TrazabilidadDrawerProvider';
import { hallazgoEtapaMeta } from 'assets/data/cicloMeta';
import { useSearchParams } from 'react-router-dom';
import useHallazgoGrupos from 'hooks/useHallazgoGrupos';

const SEVERIDAD_ANTD_COLOR = {
  BAJA: 'green',
  MEDIA: 'blue',
  ALTA: 'orange',
  CRITICA: 'red',
};

const SEVERIDAD_OPTS = ['BAJA', 'MEDIA', 'ALTA', 'CRITICA'].map((s) => ({
  value: s,
  label: s,
}));

const TIPO_OPTS = [
  { value: 'INCIDENCIA', label: 'Incidencia' },
  { value: 'SUGERENCIA', label: 'Sugerencia' },
];

const ESTADO_OPTS = [
  { value: 'ACTIVA', label: 'Activos' },
  { value: 'INACTIVA', label: 'Cerrados' },
];

const ETAPA_OPTS = [
  { value: 'DETECTADO', label: 'Detectado' },
  { value: 'PRESUPUESTADO', label: 'Presupuestado' },
  { value: 'APROBADO', label: 'Aprobado' },
  { value: 'EN_ODT', label: 'En ODT' },
  { value: 'RESUELTO', label: 'Resuelto' },
  { value: 'DESCARTADO', label: 'Descartado' },
];

const ETAPA_LABEL = {
  DETECTADO: 'Detectado',
  PRESUPUESTADO: 'Presupuestado',
  APROBADO: 'Aprobado',
  EN_ODT: 'En ODT',
  RESUELTO: 'Resuelto',
  DESCARTADO: 'Descartado',
};

const fmtDate = (d) => {
  if (!d) return '—';
  try {
    return new Date(d).toLocaleDateString('es-CL');
  } catch {
    return '—';
  }
};

// Los modales y el drawer de presupuesto operan sobre un hallazgo real. El grupo
// expone el eslabón vivo; esto es el adaptador entre ambos mundos.
const grupoAHallazgo = (g) => ({
  id: g.id_hallazgo_vivo,
  tipo: g.tipo,
  severidad: g.severidad,
  etapa: g.etapa,
  estado: g.estado,
  descripcion: g.item_titulo || g.descripcion,
  id_cliente: g.id_cliente,
  Cliente: g.Cliente,
  Ascensor: g.Ascensor,
});

const ORDEN_OPTS = [
  { value: 'prioridad', label: 'Prioridad' },
  { value: 'veces', label: 'Más repetidos' },
  { value: 'antiguedad', label: 'Más antiguos' },
  { value: 'reciente', label: 'Vistos recién' },
];

// Filtros que aplica cada lente de la franja superior. Cada lente reencuadra
// la misma lista de problemas; ninguno abre otra pantalla.
// ascensor_id, texto e item_titulo se limpian siempre: son filtros que solo
// inyectan los rankings clickeables ("Equipos" / "Tipos de falla") vía
// onFiltroPatron, y no deben sobrevivir a un cambio de lente (updateFilter
// mergea shallow sobre el estado previo, así que si no se listan acá quedan
// pegados en silencio).
const FILTROS_POR_LENTE = {
  cotizar: {
    estado: 'ACTIVA',
    etapa: 'DETECTADO',
    orden: 'prioridad',
    solo_regresiones: false,
    ascensor_id: undefined,
    texto: undefined,
    item_titulo: undefined,
  },
  equipos: {
    estado: 'ACTIVA',
    etapa: undefined,
    orden: 'prioridad',
    solo_regresiones: false,
    ascensor_id: undefined,
    texto: undefined,
    item_titulo: undefined,
  },
  tipos: {
    estado: 'ACTIVA',
    etapa: undefined,
    orden: 'prioridad',
    solo_regresiones: false,
    ascensor_id: undefined,
    texto: undefined,
    item_titulo: undefined,
  },
  regresiones: {
    estado: 'ACTIVA',
    etapa: undefined,
    orden: 'reciente',
    solo_regresiones: true,
    ascensor_id: undefined,
    texto: undefined,
    item_titulo: undefined,
  },
};

function HallazgosTable({ clienteFijo, clienteNombre, clientes = [] }) {
  const { token } = theme.useToken();
  const [searchParams] = useSearchParams();
  const [cerrarTarget, setCerrarTarget] = useState(null);
  const [severidadTarget, setSeveridadTarget] = useState(null);
  const [rankingOpen, setRankingOpen] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [presupuestoDrawerOpen, setPresupuestoDrawerOpen] = useState(false);
  // Hallazgo único a presupuestar desde el botón por fila (null = usar la
  // selección múltiple de la barra sticky).
  const [presupuestoSingle, setPresupuestoSingle] = useState(null);
  const [lente, setLente] = useState('cotizar');
  const { openHallazgo } = useTrazabilidadDrawer();

  const { data, loading, filters, setFilters, updateFilter, refetch } = useHallazgoGrupos({
    cliente_id: clienteFijo || undefined,
    // Deep-link desde la bandeja del Home (/admin/hallazgos?etapa=DETECTADO):
    // pre-aplica el filtro de etapa al montar. El lente "Para cotizar" (default)
    // no debe pisar este filtro inicial — solo se aplica cuando el usuario
    // cambia de lente explícitamente (ver cambiarLente).
    etapa: searchParams.get('etapa') || undefined,
  });

  const cambiarLente = (nuevo) => {
    setLente(nuevo);
    updateFilter(FILTROS_POR_LENTE[nuevo]);
  };

  const reabrir = async (id) => {
    try {
      await axios.post(`${API_PATH}/actividades-hallazgo/${id}/reabrir`, null, {
        headers: { Authorization: getAccessTokenApi() },
      });
      message.success('Hallazgo reabierto');
      refetch();
    } catch (err) {
      message.error(err?.response?.data?.message || 'Error al reabrir el hallazgo');
    }
  };

  // Indice por clave de grupo para acceder a los grupos seleccionados.
  const rowsByClave = useMemo(() => {
    const map = new Map();
    data.rows.forEach((g) => map.set(g.clave_grupo, g));
    return map;
  }, [data.rows]);

  const selectedRows = useMemo(
    () =>
      selectedRowKeys
        .map((k) => rowsByClave.get(k))
        .filter(Boolean)
        .map(grupoAHallazgo),
    [selectedRowKeys, rowsByClave]
  );

  // Validaciones para habilitar generar presupuesto:
  //  - Al menos 1 seleccionado.
  //  - Todos en etapa DETECTADO.
  //  - Todos del mismo cliente.
  const selectionValidation = useMemo(() => {
    if (selectedRows.length === 0) return { ok: false, reason: null };
    const notDetected = selectedRows.find((r) => r.etapa && r.etapa !== 'DETECTADO');
    if (notDetected) {
      return {
        ok: false,
        reason: `#${notDetected.id} no está en etapa Detectado (está en ${
          ETAPA_LABEL[notDetected.etapa] || notDetected.etapa
        })`,
      };
    }
    const firstCliente = selectedRows[0].Cliente?.id ?? selectedRows[0].id_cliente;
    const mixed = selectedRows.find((r) => (r.Cliente?.id ?? r.id_cliente) !== firstCliente);
    if (mixed) {
      return { ok: false, reason: 'Todos los hallazgos deben ser del mismo cliente' };
    }
    return { ok: true, reason: null };
  }, [selectedRows]);

  // Filas que van al drawer de presupuesto: una sola (botón por fila) o la
  // selección múltiple de la barra sticky.
  const presupuestoRows = useMemo(
    () => (presupuestoSingle ? [presupuestoSingle] : selectedRows),
    [presupuestoSingle, selectedRows]
  );

  const presupuestoCliente = useMemo(() => {
    const first = presupuestoRows[0];
    if (!first) return null;
    return first.Cliente?.name || clienteNombre || null;
  }, [presupuestoRows, clienteNombre]);

  const columns = useMemo(
    () => [
      {
        title: 'Tipo',
        dataIndex: 'tipo',
        key: 'tipo',
        width: 110,
        render: (t) => <Tag color={t === 'INCIDENCIA' ? 'red' : 'blue'}>{t}</Tag>,
      },
      {
        title: 'Severidad',
        dataIndex: 'severidad',
        key: 'severidad',
        width: 120,
        render: (s, r) => {
          if (r.tipo !== 'INCIDENCIA') {
            return <Typography.Text type="secondary">—</Typography.Text>;
          }
          return (
            <Tooltip title="Click para cambiar">
              <Tag
                color={SEVERIDAD_ANTD_COLOR[s] || 'default'}
                className="cursor-pointer"
                onClick={() => setSeveridadTarget(grupoAHallazgo(r))}
              >
                {s || 'SIN DEFINIR'}
              </Tag>
            </Tooltip>
          );
        },
      },
      {
        title: 'Etapa',
        dataIndex: 'etapa',
        key: 'etapa',
        width: 180,
        render: (etapa, r) => (
          <Tooltip
            title={
              etapa
                ? `${ETAPA_LABEL[etapa] || etapa}${
                    r.id_budget_actual ? ` · Presupuesto #${r.id_budget_actual}` : ''
                  } — click para ver detalle`
                : 'Click para ver detalle'
            }
            mouseEnterDelay={0.3}
          >
            <div
              role="button"
              onClick={() => openHallazgo(r.id_hallazgo_vivo, r.clave_grupo)}
              style={{ cursor: 'pointer' }}
            >
              <HallazgoTracker etapa={etapa || 'DETECTADO'} severidad={r.severidad} />
            </div>
          </Tooltip>
        ),
      },
      ...(!clienteFijo
        ? [
            {
              title: 'Cliente',
              dataIndex: ['Cliente', 'name'],
              key: 'cliente',
              ellipsis: true,
              width: 180,
              render: (_, r) => r.Cliente?.name || '—',
            },
          ]
        : []),
      {
        title: 'Ascensor',
        dataIndex: ['Ascensor', 'nombre'],
        key: 'ascensor',
        ellipsis: true,
        width: 130,
        render: (_, r) => r.Ascensor?.nombre || '—',
      },
      {
        title: 'Problema',
        key: 'problema',
        ellipsis: { showTitle: false },
        render: (_, g) => <GrupoRow grupo={g} />,
      },
      {
        title: 'Última vez',
        dataIndex: 'last_seen_at',
        key: 'last_seen_at',
        width: 110,
        render: (d) => (
          <Typography.Text className="text-xs text-slate-600">{fmtDate(d)}</Typography.Text>
        ),
      },
      {
        title: 'Acción',
        key: 'accion',
        width: 210,
        align: 'center',
        render: (_, g) => {
          if (g.estado !== 'ACTIVA') {
            return (
              <Button
                size="small"
                icon={<ReloadOutlined />}
                onClick={() => reabrir(g.id_hallazgo_vivo)}
              >
                Reabrir
              </Button>
            );
          }
          // El próximo paso de un hallazgo DETECTADO es "Presupuestar", siempre
          // visible por fila (no escondido tras la selección múltiple). Abre el
          // mismo drawer con este único hallazgo.
          const puedePresupuestar = (g.etapa || 'DETECTADO') === 'DETECTADO';
          return (
            <Space size={4}>
              {puedePresupuestar ? (
                <Button
                  size="small"
                  type="primary"
                  icon={<FileAddOutlined />}
                  onClick={() => {
                    setPresupuestoSingle(grupoAHallazgo(g));
                    setPresupuestoDrawerOpen(true);
                  }}
                >
                  {hallazgoEtapaMeta.DETECTADO.nextAction.label}
                </Button>
              ) : null}
              <Button
                size="small"
                icon={<CheckOutlined />}
                onClick={() => setCerrarTarget(grupoAHallazgo(g))}
              >
                Cerrar
              </Button>
            </Space>
          );
        },
      },
    ],
    [clienteFijo]
  );

  const rowSelection = {
    selectedRowKeys,
    onChange: setSelectedRowKeys,
    getCheckboxProps: (g) => ({
      // Solo se puede seleccionar si etapa es DETECTADO.
      disabled: g.etapa && g.etapa !== 'DETECTADO',
      name: `grupo-${g.clave_grupo}`,
    }),
    columnWidth: 40,
  };

  return (
    <div className="space-y-4">
      <FranjaPatrones
        lente={lente}
        onLenteChange={cambiarLente}
        onFiltroPatron={updateFilter}
        clienteId={clienteFijo}
      />

      {/* Filtros */}
      <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
        <Space wrap size="middle" className="w-full">
          {!clienteFijo && (
            <Select
              placeholder="Cliente"
              allowClear
              showSearch
              optionFilterProp="label"
              style={{ minWidth: 200 }}
              value={filters.cliente_id}
              onChange={(v) => updateFilter({ cliente_id: v })}
              options={clientes.map((c) => ({
                value: c.id,
                label: c.name,
              }))}
            />
          )}
          <Select
            placeholder="Tipo"
            allowClear
            style={{ minWidth: 140 }}
            value={filters.tipo}
            onChange={(v) => updateFilter({ tipo: v })}
            options={TIPO_OPTS}
          />
          <Select
            mode="multiple"
            placeholder="Severidad"
            allowClear
            style={{ minWidth: 200 }}
            value={filters.severidad}
            onChange={(v) => updateFilter({ severidad: v })}
            options={SEVERIDAD_OPTS}
            maxTagCount="responsive"
          />
          <Select
            placeholder="Etapa"
            allowClear
            style={{ minWidth: 150 }}
            value={filters.etapa}
            onChange={(v) => updateFilter({ etapa: v })}
            options={ETAPA_OPTS}
          />
          <Select
            style={{ minWidth: 130 }}
            value={filters.estado}
            onChange={(v) => updateFilter({ estado: v })}
            options={ESTADO_OPTS}
          />
          <Select
            style={{ minWidth: 150 }}
            value={filters.orden}
            onChange={(v) => updateFilter({ orden: v })}
            options={ORDEN_OPTS}
          />
          <Input.Search
            placeholder="Buscar en el problema..."
            allowClear
            style={{ minWidth: 220 }}
            prefix={<SearchOutlined className="text-slate-400" />}
            onSearch={(v) => updateFilter({ texto: v || undefined })}
          />
          {clienteFijo ? (
            <Button
              icon={<FilePdfOutlined />}
              onClick={() => setRankingOpen(true)}
              className="ml-auto"
            >
              Ranking mensual PDF
            </Button>
          ) : null}
          <Button
            onClick={refetch}
            icon={<ReloadOutlined />}
            className={clienteFijo ? undefined : 'ml-auto'}
          >
            Refrescar
          </Button>
        </Space>
      </div>

      {/* Barra de selección sticky */}
      {selectedRowKeys.length > 0 ? (
        <div
          style={{
            position: 'sticky',
            top: 12,
            zIndex: 10,
            display: 'flex',
            alignItems: 'center',
            gap: 16,
            padding: '12px 16px',
            background: '#0f172a',
            color: '#fff',
            borderRadius: 12,
            boxShadow: '0 12px 24px -8px rgba(15,23,42,0.24)',
          }}
        >
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
              padding: '4px 10px',
              background: 'rgba(255,255,255,0.12)',
              borderRadius: 999,
              fontFamily: "'IBM Plex Mono', monospace",
              fontSize: 13,
              fontWeight: 600,
            }}
          >
            <ThunderboltFilled style={{ color: token.colorWarning }} />
            {selectedRowKeys.length} seleccionado{selectedRowKeys.length === 1 ? '' : 's'}
          </div>
          {!selectionValidation.ok && selectionValidation.reason ? (
            <div style={{ flex: 1, color: '#fca5a5', fontSize: 12 }}>
              <WarningOutlined style={{ marginRight: 6 }} />
              {selectionValidation.reason}
            </div>
          ) : (
            <div style={{ flex: 1 }} />
          )}
          <Space>
            <Button
              ghost
              onClick={() => setSelectedRowKeys([])}
              style={{ borderColor: 'rgba(255,255,255,0.3)' }}
            >
              Limpiar
            </Button>
            <Tooltip title={selectionValidation.reason || ''}>
              <Button
                type="primary"
                icon={<FileAddOutlined />}
                disabled={!selectionValidation.ok}
                onClick={() => {
                  setPresupuestoSingle(null);
                  setPresupuestoDrawerOpen(true);
                }}
                style={{
                  background: token.colorWarning,
                  borderColor: token.colorWarning,
                }}
              >
                Generar presupuesto →
              </Button>
            </Tooltip>
          </Space>
        </div>
      ) : null}

      {/* Tabla */}
      <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
        <Table
          dataSource={data.rows}
          columns={columns}
          rowKey="clave_grupo"
          loading={loading}
          size="middle"
          rowSelection={rowSelection}
          locale={{
            emptyText: (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={
                  <Typography.Text type="secondary">
                    No hay problemas que coincidan con los filtros
                  </Typography.Text>
                }
              />
            ),
          }}
          pagination={{
            current: filters.page,
            pageSize: filters.pageSize,
            total: data.total,
            showSizeChanger: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} de ${total}`,
            onChange: (page, pageSize) => setFilters((prev) => ({ ...prev, page, pageSize })),
          }}
          scroll={{ x: 1100 }}
        />
      </div>

      <HallazgosCerrarModal
        target={cerrarTarget}
        onClose={() => setCerrarTarget(null)}
        onSuccess={() => {
          setCerrarTarget(null);
          refetch();
        }}
      />
      <HallazgosSeveridadModal
        target={severidadTarget}
        onClose={() => setSeveridadTarget(null)}
        onSuccess={() => {
          setSeveridadTarget(null);
          refetch();
        }}
      />
      {clienteFijo ? (
        <RankingMensualModal
          open={rankingOpen}
          onClose={() => setRankingOpen(false)}
          clienteId={clienteFijo}
          clienteNombre={clienteNombre}
        />
      ) : null}

      <PresupuestoFromHallazgosDrawer
        open={presupuestoDrawerOpen}
        hallazgos={presupuestoRows}
        clienteNombre={presupuestoCliente}
        onClose={() => {
          setPresupuestoDrawerOpen(false);
          setPresupuestoSingle(null);
        }}
        onSuccess={() => {
          setPresupuestoDrawerOpen(false);
          setPresupuestoSingle(null);
          setSelectedRowKeys([]);
          refetch();
        }}
      />
    </div>
  );
}

export default HallazgosTable;
